/**
 * Game Messaging Utility
 * 
 * Handles secure postMessage communication between the hub and game iframes.
 * Implements origin validation and authentication token passing.
 */

import { Game } from '../types/game';

/**
 * Message types for hub-game communication
 */
export enum MessageType {
  READY = 'READY',
  AUTH_TOKEN = 'AUTH_TOKEN',
  ERROR = 'ERROR',
  // IS-INV-002: game → hub only. Game cannot navigate top window (IS-INV-008 sandbox).
  // Hub handles this by navigating itself to /subscription?returnTo=<current path>.
  NAVIGATE_TO_SUBSCRIPTION = 'NAVIGATE_TO_SUBSCRIPTION',
}

/**
 * Message interfaces
 */
export interface GameReadyMessage {
  type: MessageType.READY;
}

export interface HubAuthMessage {
  type: MessageType.AUTH_TOKEN;
  token: string;
}

export interface ErrorMessage {
  type: MessageType.ERROR;
  error: string;
}

export interface NavigateToSubscriptionMessage {
  type: MessageType.NAVIGATE_TO_SUBSCRIPTION;
  returnTo?: string; // Game-provided deep-link URL to restore after subscribe (DMO flow)
}

export type GameMessage = GameReadyMessage | HubAuthMessage | ErrorMessage | NavigateToSubscriptionMessage;

/**
 * Normalizes an origin URL for comparison
 * Extracts only the origin (protocol + domain + port), ignoring any path
 * Removes protocol and lowercases the domain for comparison
 *
 * @param url - The URL to normalize
 * @returns Normalized origin string (domain + port only)
 */
function normalizeOrigin(url: string): string {
  if (!url) return '';

  try {
    // Parse the URL to extract only the origin (protocol + host + port)
    const urlObj = new URL(url);
    // Explicitly reject non-http(s) schemes (data:, file:, blob:, etc.).
    // Without this, data: and opaque-scheme URLs produce "null" which happens not to match
    // any registered origin — but the rejection should be intentional, not incidental.
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') return '';
    // Extract the full origin (protocol + host + port) and lowercase it.
    // Using origin (not host) ensures http:// and https:// variants of the same domain
    // are treated as distinct origins and not conflated.
    return urlObj.origin.toLowerCase();
  } catch {
    // Fallback for invalid URLs
    return url
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '') // Remove everything after the first slash (path)
      .toLowerCase();
  }
}

/**
 * Validates if a message origin is allowed
 * 
 * @param origin - The origin to validate
 * @param games - List of games from registry (optional for static validation)
 * @returns boolean indicating if origin is allowed
 */
export function isOriginAllowed(origin: string, games?: Game[]): boolean {
  // Check for mock mode
  const isMockMode = import.meta.env.VITE_MOCK_GAME_FRAME === 'true';
  if (isMockMode) {
    // In mock mode, allow same origin
    return origin === window.location.origin;
  }


  // Dynamic validation using game registry
  if (games && games.length > 0) {
    // Normalize the incoming origin
    const normalizedOrigin = normalizeOrigin(origin);
    
    // Check if origin matches any game's frontend URL
    const isAllowed = games
      .filter(game => game.isPublished)
      .some(game => {
        const urlPattern = game.urlPattern as { frontend?: string };
        if (!urlPattern.frontend) return false;
        
        // Normalize the expected origin and compare
        const normalizedExpected = normalizeOrigin(urlPattern.frontend);
        return normalizedOrigin === normalizedExpected;
      });
    
    if (isAllowed) {
      if (import.meta.env.VITE_LOG_GAME_MESSAGES === 'true') {
        console.log('[GameMessaging] Origin allowed via game registry:', origin);
      }
      return true;
    }
  }

  // Log rejected origin for debugging
  if (import.meta.env.VITE_LOG_GAME_MESSAGES === 'true') {
    console.warn('[GameMessaging] Rejected origin:', origin);
    if (games && games.length > 0) {
      console.warn('[GameMessaging] Expected origins:', games
        .filter(game => game.isPublished)
        .map(game => (game.urlPattern as { frontend?: string }).frontend)
        .filter(Boolean)
      );
    }
  }

  return false;
}

/**
 * Sends a message to a game iframe
 * 
 * @param iframe - The iframe element to send to
 * @param message - The message to send
 * @param targetOrigin - The target origin (defaults to iframe src origin)
 */
export function sendMessageToGame(
  iframe: HTMLIFrameElement,
  message: GameMessage,
  targetOrigin?: string
): void {
  if (!iframe.contentWindow) {
    console.error('[GameMessaging] iframe has no contentWindow');
    return;
  }

  // Extract origin from iframe src if not provided
  const origin = targetOrigin || new URL(iframe.src).origin;

  // Log message if debug mode enabled
  if (import.meta.env.VITE_LOG_GAME_MESSAGES === 'true') {
    console.log('[GameMessaging] Sending message to game:', {
      type: message.type,
      origin,
      hasToken: message.type === MessageType.AUTH_TOKEN && 'token' in message
    });
  }

  iframe.contentWindow.postMessage(message, origin);
}

/**
 * Creates a message event handler for the game frame
 *
 * @param onReady - Callback when game sends READY message
 * @param games - Optional list of games for dynamic validation
 * @param expectedSource - Optional expected source window for additional validation
 * @returns Event handler function
 */
export function createMessageHandler(
  onReady: (event: MessageEvent) => void,
  games?: Game[],
  expectedSource?: Window | null,
  onNavigateToSubscription?: (gameReturnTo?: string) => void,
): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    // Validate origin
    if (!isOriginAllowed(event.origin, games)) {
      console.error('[Security] Rejected message from unauthorized origin:', event.origin);
      return;
    }

    // Validate source window if provided
    if (expectedSource && event.source !== expectedSource) {
      console.error('[Security] Rejected message from unexpected source window');
      return;
    }

    // Validate message structure
    if (!event.data || typeof event.data !== 'object' || !event.data.type) {
      return;
    }

    // LOW-009: Validate message type is one of the expected MessageType enum values
    // This provides defense-in-depth against malicious or malformed messages
    const validMessageTypes: string[] = Object.values(MessageType);
    if (!validMessageTypes.includes(event.data.type)) {
      console.warn('[Security] Invalid message type from game:', {
        type: event.data.type,
        origin: event.origin,
        validTypes: validMessageTypes
      });
      return;
    }

    // Log received message if debug mode enabled
    if (import.meta.env.VITE_LOG_GAME_MESSAGES === 'true') {
      console.log('[GameMessaging] Received message from game:', {
        type: event.data.type,
        origin: event.origin,
        sourceValidated: expectedSource ? event.source === expectedSource : 'not-checked'
      });
    }

    // Handle NAVIGATE_TO_SUBSCRIPTION — game requests hub navigation (IS-INV-002)
    if (event.data.type === MessageType.NAVIGATE_TO_SUBSCRIPTION) {
      onNavigateToSubscription?.(event.data.returnTo);
      return;
    }

    // Handle READY message
    if (event.data.type === MessageType.READY) {
      onReady(event);
    }
  };
}

/**
 * Mock JWT token for testing
 */
export const MOCK_JWT_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJtb2NrLXVzZXItaWQiLCJuYW1lIjoiTW9jayBVc2VyIiwiZW1haWwiOiJtb2NrQGV4YW1wbGUuY29tIiwiaWF0IjoxNjI5MTI5NjAwLCJleHAiOjE2MjkxMzMyMDB9.mock-signature-for-testing';

/**
 * Gets auth token for game communication
 * 
 * @param getToken - Clerk's getToken function
 * @returns Promise resolving to JWT token or mock token
 */
export async function getAuthToken(
  getToken?: () => Promise<string | null>
): Promise<string> {
  // Check for mock mode
  const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';
  
  if (isMockAuth) {
    console.log('[GameMessaging] Using mock JWT token for testing');
    return MOCK_JWT_TOKEN;
  }

  if (!getToken) {
    throw new Error('getToken function not provided');
  }

  const token = await getToken();
  if (!token) {
    throw new Error('Failed to get authentication token');
  }

  return token;
}